import { FC } from 'react';
import {
  ComponentRendering,
  GetStaticComponentProps,
  withDatasourceCheck,
} from '@sitecore-jss/sitecore-jss-nextjs';
import classNames from 'classnames';
import { sanitize } from 'isomorphic-dompurify';

import * as icons from '@gn-audio/icons';
import { Button, JabraIcon } from '@gn-audio/jss-components';
import { isColorBright, resolveColorProperty } from '@gn-audio/jss-components/utils';

import { Eyebrow } from 'common/components/Eyebrow';
import { mapSharedPropertiesToFields } from 'common/getters';
import { SitecoreFieldTypes } from 'common/types/sitecore';
import { Disclaimer } from 'components/Product/StatsSpot/components/Disclaimer';
import { Stat } from 'components/Product/StatsSpot/components/Stat';
import { StatsSpotFields, StatsSpotProps } from 'components/Product/StatsSpot/statsSpotTypes';

const StatsSpot: FC<StatsSpotProps> = ({ fields }: StatsSpotProps) => {
  const {
    backgroundColor,
    eyebrowText,
    headline,
    description,
    stats,
    footerText,
    ctaButtonLink,
    ctaButtonLabel,
    disclaimers,
  } = fields;

  const resolvedBackgroundColor = resolveColorProperty(backgroundColor?.value);
  const isBackgroundBright = isColorBright(resolvedBackgroundColor);
  const textColorClass = isBackgroundBright ? 'text-black' : 'text-white';
  const themeClass = isBackgroundBright ? 'light' : 'dark';

  return (
    <article style={{ backgroundColor: resolvedBackgroundColor }} className={themeClass}>
      <div
        className={classNames(
          'container flex w-full flex-col items-center justify-center md:w-3/4 lg:w-2/3',
          textColorClass
        )}
      >
        <header>
          {!!eyebrowText?.value && (
            <Eyebrow align="center" text={eyebrowText.value} className="mb-12" />
          )}
          {!!headline?.value && <h2 className="heading-2 mb-6 text-center">{headline.value}</h2>}
          {!!description?.value && (
            <p
              className="paragraph-large mb-6 text-center md:mb-12"
              dangerouslySetInnerHTML={{ __html: description.value }}
            />
          )}
        </header>
        {!!stats?.length && (
          <section className="mb-6 flex flex-col gap-3 md:gap-6">
            {stats.map((stat) => (
              <div key={stat.id}>
                <Stat
                  value={stat.value}
                  valueFontColor={stat.valueFontColor}
                  description={stat.description}
                />
              </div>
            ))}
          </section>
        )}
        <footer className="flex flex-col items-center justify-center">
          {!!footerText?.value && (
            <p
              className="paragraph-large mb-12 text-center"
              dangerouslySetInnerHTML={{ __html: footerText.value }}
            />
          )}
          {!!ctaButtonLink?.value?.href && (
            <Button
              size="l"
              asLink
              variant="secondary"
              iconPlacement="right"
              className="mb-12 inline-flex"
              href={ctaButtonLink.value.href}
              icon={<JabraIcon Icon={icons.icon_arrow_right} />}
            >
              {ctaButtonLabel?.value}
            </Button>
          )}
          {!!disclaimers.length && (
            <div className="flex flex-row flex-wrap items-center justify-center gap-2">
              {disclaimers.map((disclaimer) => (
                <Disclaimer text={disclaimer.text} key={disclaimer.id} />
              ))}
            </div>
          )}
        </footer>
      </div>
    </article>
  );
};

export const getStaticProps: GetStaticComponentProps = async (rendering: ComponentRendering) => {
  try {
    const { fields: dataSource, params = {} } = rendering;

    const statsSpotData = dataSource as SitecoreFieldTypes;
    const rawFields = mapSharedPropertiesToFields<StatsSpotFields>(statsSpotData);
    const fields = {
      ...rawFields,
      description: {
        ...rawFields.description,
        value: sanitize(rawFields.description?.value),
      },
      disclaimers: rawFields.disclaimers.map((disclaimer) => ({
        ...disclaimer,
        disclaimerText: {
          ...disclaimer.text,
          value: sanitize(disclaimer.text?.value),
        },
      })),
      footerText: { ...rawFields.footerText, value: sanitize(rawFields.footerText?.value) },
      stats: rawFields.stats.map((stat) => ({
        ...stat,
        statDescription: { ...stat.description, value: sanitize(stat.description?.value) },
      })),
    };

    return { fields, params };
  } catch (error) {
    console.log(error);
    const { params = {} } = rendering;
    return { params };
  }
};

export default withDatasourceCheck()(StatsSpot);
